import React, { useEffect, useState } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    CircularProgress,
    Typography,
    Grid,
    Paper,
} from "@mui/material";
import DeleteIcon from '@mui/icons-material/Delete';
import UploadImage from "../../../components/images/UploadImage";
import DeliveryImages from "../../../components/delivery/DeliveryImages";
import { getImagesByBoxId, saveBoxImage, deleteBoxImage } from "../../../config";
import { Alerts } from "../../../components/alerts/Alerts.tsx";

const getImageSrc = (img) => img?.url || '';
const getImageId = (img) => img?.id || null;

const ModifyImagesDialog = ({ open, onClose, token, auth, id_box, canModifyImages }) => {
    const [pickupImages, setPickupImages] = useState([]);
    const [deliveryImages, setDeliveryImages] = useState([]);
    const [loading, setLoading] = useState(true);
    const [images, setImages] = useState([]);
    const [previews, setPreviews] = useState([]);
    const [uploading, setUploading] = useState(false);

    const { alertSuccess, showErrorRequest } = Alerts();

    const fetchImages = async () => {
        setLoading(true);
        try {
            const resp = await getImagesByBoxId(token, id_box);
            const data = await resp.json();
            setPickupImages(Array.isArray(data.pickup_images) ? data.pickup_images : []);
            setDeliveryImages(Array.isArray(data.delivery_images) ? data.delivery_images : []);
        } catch (err) {
            setPickupImages([]);
            setDeliveryImages([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (open && id_box) {
            setImages([]);
            setPreviews([]);
            fetchImages();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, id_box]);

    const handleDelete = async (id_image) => {
        if (!id_image) return;
        try {
            setLoading(true);
            const resp = await deleteBoxImage(token, id_image);
            if (resp.ok) {
                alertSuccess("Éxito", "Imagen eliminada correctamente");
                await fetchImages();
            } else {
                if (resp.status === 401 || resp.status === 403) {
                    showErrorRequest("No tiene autorización o su sesión ha expirado", auth);
                } else {
                    const errorData = await resp.json().catch(() => null);
                    showErrorRequest(errorData?.detail || errorData?.message || "No fue posible eliminar la imagen");
                }
                setLoading(false);
            }
        } catch (err) {
            showErrorRequest(err.message || "Error de conexión");
            setLoading(false);
        }
    };

    const handleUpload = async () => {
        if (images.length === 0) return;
        try {
            setUploading(true);
            const resp = await saveBoxImage(token, { images }, id_box);
            if (resp.ok) {
                const data = await resp.json();
                alertSuccess("Éxito", data.message);
                setImages([]);
                setPreviews([]);
                await fetchImages();
            } else {
                if (resp.status === 401 || resp.status === 403) {
                    showErrorRequest("No tiene autorización o su sesión ha expirado", auth);
                } else {
                    const errorData = await resp.json().catch(() => null);
                    showErrorRequest(errorData?.detail || errorData?.message || "No fue posible subir las imágenes");
                }
            }
        } catch (err) {
            showErrorRequest(err.message || "Error de conexión");
        } finally {
            setUploading(false);
        }
    };

    const renderImageGroup = (title, list) => (
        list.length > 0 && (
            <Box mt={3}>
                <Typography variant="h6" align="center" gutterBottom>{title}</Typography>
                <Box display="flex" justifyContent="center">
                    <Grid container spacing={2} justifyContent="center" sx={{ maxWidth: "1400px" }}>
                        {list.map((img, index) => {
                            const src = getImageSrc(img);
                            const id_image = getImageId(img);
                            return (
                                <Grid item xs={12} sm={6} md={4} lg={3} key={id_image ?? index}>
                                    <Paper elevation={3} sx={{ p: 1 }}>
                                        <Box
                                            sx={{
                                                width: '100%',
                                                height: '300px',
                                                overflow: 'hidden',
                                                display: 'flex',
                                                justifyContent: 'center',
                                                alignItems: 'center',
                                                borderRadius: '8px',
                                            }}
                                        >
                                            <img
                                                src={src}
                                                alt={`Imagen ${index + 1}`}
                                                style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                                            />
                                        </Box>
                                        <Box display="flex" justifyContent="center" mt={1}>
                                            <Button
                                                size="small"
                                                color="error"
                                                variant="contained"
                                                startIcon={<DeleteIcon />}
                                                disabled={!canModifyImages || !id_image}
                                                onClick={() => handleDelete(id_image)}
                                            >
                                                Eliminar
                                            </Button>
                                        </Box>
                                    </Paper>
                                </Grid>
                            );
                        })}
                    </Grid>
                </Box>
            </Box>
        )
    );

    return (
        <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
            <DialogTitle>Modificar Imágenes</DialogTitle>
            <DialogContent>
                {loading ? (
                    <Box display="flex" justifyContent="center" mt={4}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <Box>
                        {pickupImages.length === 0 && deliveryImages.length === 0 && (
                            <Typography align="center" mt={2}>
                                No hay imagenes para mostrar.
                            </Typography>
                        )}
                        {renderImageGroup("Imágenes de Pickup", pickupImages)}
                        {renderImageGroup("Imágenes de Entrega", deliveryImages)}

                        {canModifyImages && (
                            <Box mt={4}>
                                <Typography variant="h6" gutterBottom>
                                    Subir nuevas imágenes
                                </Typography>
                                <UploadImage
                                    images={images}
                                    setImages={setImages}
                                    setPreviews={setPreviews}
                                    mostrarErrorRequest={(msg) => showErrorRequest(msg, auth)}
                                    minimo={1}
                                />
                                {previews.length > 0 && (
                                    <DeliveryImages images={previews} title="Vista previa" />
                                )}
                                {images.length > 0 && (
                                    <Box display="flex" justifyContent="end" mt={2}>
                                        <Button
                                            variant="contained"
                                            color="secondary"
                                            disabled={uploading}
                                            onClick={handleUpload}
                                        >
                                            {uploading ? <CircularProgress size={20} /> : "Guardar imágenes"}
                                        </Button>
                                    </Box>
                                )}
                            </Box>
                        )}
                    </Box>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} color="primary" variant="outlined">
                    Cerrar
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default ModifyImagesDialog;
